import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api, useLanguage } from '../context/AppContext';
import { Button } from './ui/button';
import { AlertTriangle, Timer, UtensilsCrossed, X, Loader2, Clock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import useCameraViewport from '../utils/useCameraViewport';

/**
 * useFastingGuard — Anti-cheat hook.
 * Before saving a meal with >15 kcal, checks if user is currently fasting.
 * If yes, shows a warning popup with 3 options:
 *   1. "Rompre le jeune" — breaks the fast (stops timer), meal logs
 *   2. "J'ai mange avant mon jeune" — meal logs retroactively, timer KEEPS running
 *   3. "Annuler" — meal is NOT logged
 *
 * Returns: { checkFastingGuard(calories), FastingGuardModal }
 *   - checkFastingGuard(cal): returns true if OK to proceed, false if cancelled
 */
export function useFastingGuard() {
  const { language } = useLanguage();
  const [showGuard, setShowGuard] = useState(false);
  const [guardData, setGuardData] = useState(null);
  // IMPORTANT: useRef (not useState) — setState with a function treats it as a setter
  // callback and turns `resolve` into `(prev) => resolve`, which means calling
  // `resolveRef(false)` would return `resolve` but never invoke it. The previous
  // implementation with useState made Cancel silently hang the flow.
  const resolveRef = useRef(null);
  const [breaking, setBreaking] = useState(false);
  const fr = language === 'fr';

  // Iter. 406.g — body scroll lock while dialog open (prevents iOS rubber-band
  // from sliding the underlying meal flow behind the dialog).
  useCameraViewport({ open: showGuard, lockBodyScroll: true });

  const closeAndResolve = (value) => {
    setShowGuard(false);
    const r = resolveRef.current;
    resolveRef.current = null;
    if (r) r(value);
  };

  const checkFastingGuard = useCallback(async (calories = 0) => {
    // If calories <= 15, skip (black coffee, electrolytes OK)
    if (calories <= 15) return true;

    try {
      const res = await api.get('/fasting/check-fasting-guard');
      if (!res.data.is_fasting) return true;

      // User IS in fasting window — show warning
      setGuardData(res.data);
      setShowGuard(true);

      return new Promise((resolve) => {
        resolveRef.current = resolve;
      });
    } catch {
      // If check fails, let the meal through (don't block on errors)
      return true;
    }
  }, []);

  const handleBreakFast = async () => {
    setBreaking(true);
    try {
      const res = await api.post('/fasting/break', {
        reason: 'meal_logged',
        calories: 0,
      });
      toast.success(res.data.message || (fr ? 'Jeune arrete' : 'Fast stopped'));
      closeAndResolve(true);
    } catch {
      toast.error(fr ? 'Erreur' : 'Error');
    } finally { setBreaking(false); }
  };

  // User says the meal was eaten BEFORE the current fast started.
  // We let the meal log normally but do NOT stop the fasting timer.
  const handleMealBeforeFast = () => {
    toast.info(
      fr
        ? 'Repas ajoute aux macros. Ton jeune continue a tourner.'
        : 'Meal added to macros. Your fast keeps running.'
    );
    closeAndResolve(true);
  };

  const handleCancel = () => {
    closeAndResolve(false);
  };

  const FastingGuardModal = createPortal(
    <AnimatePresence>
      {showGuard && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[2147483000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          style={{
            pointerEvents: 'auto',
            paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
          }}
          data-testid="fasting-guard-modal"
          onClick={(e) => e.target === e.currentTarget && handleCancel()}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            style={{ pointerEvents: 'auto' }}
            className="bg-card border-2 border-amber-500/50 rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-[0_0_40px_rgba(245,158,11,0.15)] relative"
          >
            <div className="text-center">
              <div className="w-14 h-14 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                <AlertTriangle className="w-7 h-7 text-amber-400" />
              </div>
              <h3 className="font-heading text-lg uppercase tracking-tight text-amber-400" data-testid="fasting-guard-title">
                {fr ? 'ALERTE JEUNE ACTIF' : 'ACTIVE FASTING ALERT'}
              </h3>
            </div>

            <p className="text-sm text-center text-muted-foreground">
              {fr
                ? 'Tu es actuellement en periode de jeune. As-tu mange ce repas AVANT le debut de ton jeune, ou maintenant (ce qui rompra le jeune) ?'
                : 'You are currently in a fasting period. Did you eat this meal BEFORE your fast started, or now (which will break the fast)?'}
            </p>

            {guardData && (
              <div className="flex items-center justify-center gap-3 text-xs">
                <div className="flex items-center gap-1 text-indigo-400">
                  <Timer className="w-3 h-3" />
                  {guardData.protocol}
                </div>
                <span className="text-muted-foreground/40">|</span>
                <span className="text-amber-400 font-bold">{Math.round(guardData.progress_pct)}%</span>
                <span className="text-muted-foreground/40">|</span>
                <span className="text-muted-foreground">
                  {Math.floor((guardData.time_remaining_minutes || 0) / 60)}h
                  {((guardData.time_remaining_minutes || 0) % 60).toString().padStart(2, '0')} {fr ? 'restant' : 'left'}
                </span>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {/* Primary safe option: retroactive log, keeps the fast running */}
              <Button
                onClick={handleMealBeforeFast}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white h-11"
                data-testid="fasting-guard-prefast-btn"
              >
                <Clock className="w-4 h-4 mr-2" />
                {fr ? "J'ai mange AVANT mon jeune" : 'I ate this BEFORE my fast'}
              </Button>
              {/* Destructive option: actually break the fast */}
              <Button
                onClick={handleBreakFast}
                disabled={breaking}
                className="w-full bg-amber-600 hover:bg-amber-500 text-white h-11"
                data-testid="fasting-guard-break-btn"
              >
                {breaking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UtensilsCrossed className="w-4 h-4 mr-2" />}
                {fr ? 'Oui, rompre le jeune maintenant' : 'Yes, break the fast now'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                className="w-full h-11 border-border"
                data-testid="fasting-guard-cancel-btn"
              >
                <X className="w-4 h-4 mr-2" />
                {fr ? "Annuler l'ajout" : 'Cancel'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    typeof document !== 'undefined' ? document.body : null
  );

  return { checkFastingGuard, FastingGuardModal };
}


/**
 * FastingCompletePopup — Shown when the fasting timer reaches 0.
 * Provides nutritional guidance for breaking the fast.
 */
export function FastingCompletePopup({ open, onClose, eatingWindowMinutes, language }) {
  // Iter. 406.g — body scroll lock while popup open
  useCameraViewport({ open, lockBodyScroll: true });
  const fr = language === 'fr';
  if (!open) return null;

  const eatingHours = Math.floor((eatingWindowMinutes || 0) / 60);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          style={{
            paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
            paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))',
          }}
          data-testid="fasting-complete-popup"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-card border-2 border-emerald-500/50 rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-[0_0_40px_rgba(16,185,129,0.2)]"
          >
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-3 animate-bounce">
                <Timer className="w-8 h-8 text-emerald-400" />
              </div>
              <h3 className="font-heading text-xl uppercase tracking-tight text-emerald-400" data-testid="fasting-complete-title">
                {fr ? 'MISSION ACCOMPLIE !' : 'MISSION ACCOMPLISHED!'}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {fr ? 'Ton jeune est termine. Voici comment briser la glace intelligemment.' : 'Your fast is complete. Here\'s how to break it smartly.'}
              </p>
            </div>

            <div className="space-y-2.5">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs font-bold text-emerald-400 mb-1">
                  {fr ? 'Conseil #1 — Premier repas' : 'Tip #1 — First meal'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fr
                    ? 'Commence par des proteines et des bons gras pour éviter un pic de sucre. Oeufs, yaourt grec, poulet grille, avocat.'
                    : 'Start with proteins and healthy fats to avoid a sugar spike. Eggs, Greek yogurt, grilled chicken, avocado.'}
                </p>
              </div>

              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs font-bold text-amber-400 mb-1">
                  {fr ? 'Conseil #2 — Ta fenêtre' : 'Tip #2 — Your window'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {fr
                    ? `Il te reste ${eatingHours}h dans ta fenetre d'alimentation aujourd'hui. Assure-toi de prioriser des aliments denses pour atteindre tes cibles de macros !`
                    : `You have ${eatingHours}h left in your eating window today. Make sure to prioritize nutrient-dense foods to hit your macro targets!`}
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={() => {
                  onClose('log_meal');
                }}
                className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white"
                data-testid="fasting-complete-log-meal-btn"
              >
                <UtensilsCrossed className="w-4 h-4 mr-2" />
                {fr ? 'Loguer mon premier repas' : 'Log my first meal'}
              </Button>
              <Button
                variant="outline"
                onClick={() => onClose('dismiss')}
                className="w-full"
                data-testid="fasting-complete-dismiss-btn"
              >
                {fr ? 'Plus tard' : 'Later'}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
